import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { NavGuardProvider } from './contexts/NavGuardContext';
import { GradientBars } from './components/ui/gradient-bars';
import TopNavbar from './components/TopNavbar';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import ProposalGenerator from './pages/ProposalGenerator';
import ProposalDashboard from './pages/ProposalDashboard';
import ProposalList from './pages/ProposalList';
import ProposalViewer from './pages/ProposalViewer';
import ProposalEditor from './pages/ProposalEditor';
import ProposalOutlineBuilder from './pages/ProposalOutlineBuilder';
import AnalysisViewer from './pages/AnalysisViewer';
import AnalysisRunner from './pages/AnalysisRunner';
import CategoryManager from './pages/CategoryManager';
import Management from './pages/Management';
import './App.css';
import LPGenerator from './pages/LPGenerator';
import LessonPlanPage from './pages/LessonPlanPage';
import SurveyPage from './pages/survey/SurveyPage';
import SurveyResults from './pages/survey/SurveyResults';
import SurveyHistory from './pages/survey/SurveyHistory';
import ChatbotPage from './pages/ChatbotPage';

function AppContent() {
  const location = useLocation();
  const showGradientBars = location.pathname !== '/proposal-outline' && !location.pathname.startsWith('/lesson-plan');
  
  return (
    <div className="app relative">
      {showGradientBars && (
        <GradientBars bars={30} colors={['rgba(12, 28, 205, 0.3)', 'rgba(71, 10, 177, 0.3)', 'rgba(5, 150, 105, 0.5)', 'rgba(255, 255, 255, 0.1)']} />
      )}
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={
              <ProtectedRoute requireAuth={false}>
                <Login />
              </ProtectedRoute>
            } />
            <Route path="/register" element={
              <ProtectedRoute requireAuth={false}>
                <Register />
              </ProtectedRoute>
            } />
            
            {/* Protected Routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <Navigate to="/proposal-outline" replace />
                  </main>
                </>
              </ProtectedRoute>
            } />
            
            <Route path="/proposal-generator" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <ProposalGenerator />
                  </main>
                </>
              </ProtectedRoute>
            } />
            
            <Route path="/proposal-dashboard" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <ProposalDashboard />
                  </main>
                </>
              </ProtectedRoute>
            } />
            <Route path="/lp" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <LPGenerator />
                  </main>
                </>
              </ProtectedRoute>
            } />
            <Route path="/lesson-plan" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <LessonPlanPage />
                  </main>
                </>
              </ProtectedRoute>
            } />
            <Route path="/lesson-plan/:planId" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <LessonPlanPage />
                  </main>
                </>
              </ProtectedRoute>
            } />
            <Route path="/proposal-outline" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <ProposalOutlineBuilder />
                  </main>
                </>
              </ProtectedRoute>
            } />
            <Route path="/proposal-outline/:planId" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <ProposalOutlineBuilder />
                  </main>
                </>
              </ProtectedRoute>
            } />
            
            <Route path="/proposals" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <ProposalList />
                  </main>
                </>
              </ProtectedRoute>
            } />
            
            <Route path="/proposal-viewer/:id" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <ProposalViewer />
                  </main>
                </>
              </ProtectedRoute>
            } />
            
            <Route path="/proposal-editor/:id" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <ProposalEditor />
                  </main>
                </>
              </ProtectedRoute>
            } />
            
            <Route path="/analysis" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <AnalysisViewer />
                  </main>
                </>
              </ProtectedRoute>
            } />
            
            <Route path="/analysis-runner" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <AnalysisRunner />
                  </main>
                </>
              </ProtectedRoute>
            } />
            
            <Route path="/category-manager" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <CategoryManager />
                  </main>
                </>
              </ProtectedRoute>
            } />
            
            <Route path="/management" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <Management />
                  </main>
                </>
              </ProtectedRoute>
            } />
            
            <Route path="/survey" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <SurveyPage />
                  </main>
                </>
              </ProtectedRoute>
            } />
            
            <Route path="/survey/results/:responseId" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <SurveyResults />
                  </main>
                </>
              </ProtectedRoute>
            } />
            
            <Route path="/survey/history" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <SurveyHistory />
                  </main>
                </>
              </ProtectedRoute>
            } />
            
            <Route path="/chatbot" element={
              <ProtectedRoute>
                <>
                  <TopNavbar />
                  <main className="main-content">
                    <ChatbotPage />
                  </main>
                </>
              </ProtectedRoute>
            } />
          </Routes>
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'rgba(255, 255, 255, 0.95)',
              color: '#1f2937',
              border: '1px solid #e5e7eb',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            },
          }}
        />
              </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <NavGuardProvider>
          <AppContent />
        </NavGuardProvider>
      </Router>
    </AuthProvider>
  );
}

export default App; 